import {IFormControl} from '@rxweb/types';
import {OrderQtyDecimalsInputParams} from '../../../components/order-qty-multi-currency';
import {AspreaderSecurityLeg} from '../../../components/security/aspreader-security';
import {DisplayDecimal, DisplayDecimalWithCurrency} from '../../../components/symbol-decimals';
import {
    createColumnsDict,
    Table,
    TABLE_COLUMN_SIZING_FN,
    TableColumn,
    TableColumnConfig,
    TableColumnsDefMap,
    TableColumnsSizingStep
} from '../../../components/tables/table';
import {Side} from '../../../enums/side';
import {ExchangeAccount, ExchangeAccountBase} from '../../../models/exchange-account';
import {Security} from '../../../models/security';
import {SecurityKey} from '../../../models/security-key';

export interface AsecurityAccountsMapTableRowSide {
    origLegSide: Side;
    orderSide: Side;
    resultSide: Side;
    label: string;
    classes: string;
}

export interface AsecurityAccountsMapTableRow {
    leg: AspreaderSecurityLeg;
    securityKey: SecurityKey;
    securityKeyStr: string;
    accountControl: IFormControl<ExchangeAccountBase>;
    leverageControl: IFormControl<number>;
    maxShowControl?: IFormControl<number>; // For active quoting leg only, for optimization reasons
    maxUnfilledControl?: IFormControl<number>; // For active quoting leg only, for optimization reasons
    accountFilter: (account: ExchangeAccount) => boolean;
    isMakerOnly: boolean;
    displayPriceRatio: DisplayDecimal;

    /**
     * Initialized/updated in {@link AsecurityAccountsMapComponent.createRowProcessingStream}.
     */
    security: Security;
    displaySide: AsecurityAccountsMapTableRowSide;
    isLeverageAllowed: boolean;
    qtyFormatter: (value: number) => DisplayDecimalWithCurrency;
    qtyInputParams?: OrderQtyDecimalsInputParams; // For active quoting leg only, for optimization reasons
    qtyCurrency?: string;
    displayAccountName: string;
    qty: number;
}

export type AsecurityAccountsMapTable = Table<AsecurityAccountsMapTableColumnKey, AsecurityAccountsMapTableRow>;

export enum AsecurityAccountsMapTableColumnKey {
    SYMBOL      = 'SYMBOL',
    FLAGS       = 'FLAGS',
    SIDE        = 'SIDE',
    QTY         = 'QTY',
    PRICE_RATIO = 'PRICE_RATIO',
    ACCOUNT     = 'ACCOUNT',
    LEVERAGE    = 'LEVERAGE'
}

export interface AsecurityAccountsMapTableColumn extends TableColumn {
    key: AsecurityAccountsMapTableColumnKey;
}

export interface AsecurityAccountsMapTableColumnConfig extends TableColumnConfig {
    key: AsecurityAccountsMapTableColumnKey;
}

export enum AsecurityAccountsMapSizeGroup {
    SYMBOL,
    FLAGS,
    SIDE,
    QTY,
    PRICE_RATIO,
    ACCOUNT,
    LEVERAGE
}

export const ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DEF: AsecurityAccountsMapTableColumn[] = [
    {
        key:         AsecurityAccountsMapTableColumnKey.FLAGS,
        label:       '',
        width:       '5%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.FLAGS
    },
    {
        key:         AsecurityAccountsMapTableColumnKey.SYMBOL,
        label:       'components.asecurity-accounts-map.symbol',
        width:       '30%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.SYMBOL
    },
    {
        key:         AsecurityAccountsMapTableColumnKey.SIDE,
        label:       'enums.side.label',
        width:       '10%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.SIDE
    },
    {
        key:         AsecurityAccountsMapTableColumnKey.QTY,
        label:       'components.asecurity-accounts-map.quantity',
        width:       '10%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.QTY
    },
    {
        key:         AsecurityAccountsMapTableColumnKey.PRICE_RATIO,
        label:       'components.asecurity-accounts-map.price_ratio',
        width:       '10%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.PRICE_RATIO
    },
    {
        key:         AsecurityAccountsMapTableColumnKey.ACCOUNT,
        label:       'components.asecurity-accounts-map.account',
        width:       '30%',
        isRequired:  true,
        sizeGroupId: AsecurityAccountsMapSizeGroup.ACCOUNT
    },
    {
        label:       'components.leverage.leverage_label',
        key:         AsecurityAccountsMapTableColumnKey.LEVERAGE,
        width:       '10%',
        sizeGroupId: AsecurityAccountsMapSizeGroup.LEVERAGE
    }
];

export const ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DICT:
                 TableColumnsDefMap<AsecurityAccountsMapTableColumnKey, AsecurityAccountsMapTableColumn> =
                 createColumnsDict(ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_DEF);

const ASECURITY_ACCOUNTS_MAP_COLUMN_SIZE_GROUPS = [
    AsecurityAccountsMapSizeGroup.SYMBOL,
    AsecurityAccountsMapSizeGroup.FLAGS,
    AsecurityAccountsMapSizeGroup.ACCOUNT,
    AsecurityAccountsMapSizeGroup.LEVERAGE,
    AsecurityAccountsMapSizeGroup.SIDE,
    AsecurityAccountsMapSizeGroup.QTY,
    AsecurityAccountsMapSizeGroup.PRICE_RATIO
];

export const ASECURITY_ACCOUNTS_MAP_TABLE_COLUMNS_SIZING:
                 Array<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>> = [
    {
        sizeGroupId:  AsecurityAccountsMapSizeGroup.SYMBOL,
        getGroupSize: TABLE_COLUMN_SIZING_FN.fixedWidth(70)
    },
    {
        sizeGroupId:  AsecurityAccountsMapSizeGroup.ACCOUNT,
        getGroupSize: TABLE_COLUMN_SIZING_FN.fixedWidth(130)
    },
    {
        sizeGroupId:  AsecurityAccountsMapSizeGroup.LEVERAGE,
        getGroupSize: TABLE_COLUMN_SIZING_FN.fixedWidth(60)
    },
    ...ASECURITY_ACCOUNTS_MAP_COLUMN_SIZE_GROUPS
        .filter(sizeGroupId => sizeGroupId !== AsecurityAccountsMapSizeGroup.FLAGS)
        .map<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>>(
            sizeGroupId => ({
                sizeGroupId,
                getGroupSize: TABLE_COLUMN_SIZING_FN.fixedWidth(32)
            })),
    ...ASECURITY_ACCOUNTS_MAP_COLUMN_SIZE_GROUPS.map<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>>(
        sizeGroupId => ({
            sizeGroupId,
            getGroupSize: TABLE_COLUMN_SIZING_FN.lazyFitContent()
        }))
];

export const ASECURITY_ACCOUNTS_MAP_READONLY_TABLE_COLUMNS_SIZING:
                 Array<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>> = [
    ...ASECURITY_ACCOUNTS_MAP_COLUMN_SIZE_GROUPS.map<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>>(
        sizeGroupId => ({
            sizeGroupId,
            getGroupSize: TABLE_COLUMN_SIZING_FN.fixedWidth(32)
        })),
    ...ASECURITY_ACCOUNTS_MAP_COLUMN_SIZE_GROUPS.map<TableColumnsSizingStep<AsecurityAccountsMapSizeGroup>>(
        sizeGroupId => ({
            sizeGroupId,
            getGroupSize: TABLE_COLUMN_SIZING_FN.lazyFitContent()
        }))
];
